import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Credential, LlmProvider, Model } from '../Types';
import { DLPState } from '../Types/Enums';
import toast from 'react-hot-toast';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { UserSettings } from '../Types';
import { modelService } from '../Services/modelService';
import { settingsService } from '../Services/settingsService';
import { credentialsService } from '../Services/credentialsService';
import { setReloadPrompts } from '../Store/features/environment/environmentSlice';

const useModel = () => {
    const navigate = useNavigate();
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const dispatch = useAppDispatch();
    const location = useLocation();

    let { modelIdentifier } = useParams();
    const [modelSnapshot, setModelSnapshot] = useState<Model>();
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [llmProviders, setLlmProviders] = useState<LlmProvider[]>([]);
    const [credentials, setCredentials] = useState<Credential[]>([]);
    const icapAddress = useAppSelector((state: RootState) => state.settings.icapAddress);
    const sensitivityLabels = useAppSelector((state: RootState) => state.settings.sensitivityLabels);
    const [mimeTypesCopied, setMimeTypesCopied] = useState(false);
    const [loadingState, setLoadingState] = useState({
        modelLoaded: false,
        llmModelLoaded: false,
        credentialsLoaded: false,
    });
    const [model, setModel] = useState<Model>({
        identifier: '',
        title: '',
        provider: '',
        value: '',
        costInput: 0.0,
        costOutput: 0.0,
        tokensPerContext: 0,
        isVisionModel: false,
        canStream: true,
        temperature: 1.0,
        topP: 1.0,
        reasoningEffort: 'none',
        settings: {},
        image: '',
        dlp: DLPState.dlp_optional,
        sensitiveFileDlpActive: false,
        icapDlpActive: false,
        sensitivityLabels: [],
        hasPrompts: false,
        isAssistantModel: false,
        useStreamOptions: true,
        useUser: true,
        additionalParameters: {},
        allowedMimeTypes: [],
        credential: null,
    });
    const [additionalParamsText, setAdditionalParamsText] = useState('{}');
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [allMimeTypesWithFileExtensions, setAllMimeTypesWithFileExtensions] = useState<{ [key: string]: string[] }>(
        {},
    );

    const notifySuccess = useCallback(() => {
        toast.success(i18n.translate({ id: 'toast', sourceName: 'Model' }));
    }, [i18n]);

    const handleChangeModel = useCallback((key: keyof Model, value: any) => {
        setModel((prevModel) => ({
            ...prevModel,
            [key]: value,
        }));
    }, []);

    const updateModel = useCallback((updates: Partial<Model>) => {
        setModel((prevModel) => ({
            ...prevModel,
            ...updates,
        }));
    }, []);

    const handleLlmProviderDropdownChange = useCallback(
        (value: string) => {
            if (value === '---') {
                handleChangeModel('provider', '');
            } else {
                handleChangeModel('provider', value);
            }

            const selectedLlmProvider = llmProviders.find((provider) => provider.identifier === value);

            if (selectedLlmProvider) {
                handleChangeModel('settings', {});
            }
        },
        [llmProviders, handleChangeModel],
    );

    const handleCredentialDropdownChange = useCallback(
        (value: string) => {
            const selectedCredential = credentials.find((credential) => credential.identifier === value);
            if (selectedCredential) {
                handleChangeModel('credential', selectedCredential);
            }
        },
        [credentials, handleChangeModel],
    );

    const handleImageChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];
                setImagePreview(URL.createObjectURL(file));
                handleChangeModel('image', file);
            }
        },
        [handleChangeModel],
    );

    const handleRemoveImage = useCallback(() => {
        setImagePreview(null);
        handleChangeModel('image', null);
    }, [handleChangeModel]);

    const handleSave = useCallback(async () => {
        if (!userSettings.isLoggedIn) return;
        const data = new FormData();
        data.append('title', model.title);
        data.append('provider', model.provider);
        data.append('value', model.value);
        data.append('costInput', model.costInput ? (model.costInput / 1000000).toString() : '0');
        data.append('costOutput', model.costOutput ? (model.costOutput / 1000000).toString() : '0');
        data.append('tokensPerContext', model.tokensPerContext ? model.tokensPerContext.toString() : '0');
        data.append('isVisionModel', model.isVisionModel ? 'true' : 'false');
        data.append('canStream', model.canStream ? 'true' : 'false');
        data.append(
            'temperature',
            model.temperature !== null && model.temperature !== undefined ? model.temperature.toString() : '1.0',
        );
        data.append('topP', model.topP !== null && model.topP !== undefined ? model.topP.toString() : '1.0');
        data.append('reasoningEffort', model.reasoningEffort);
        data.append('dlp', model.dlp);
        data.append('sensitiveFileDlpActive', model.sensitiveFileDlpActive ? 'true' : 'false');
        data.append('icapDlpActive', model.icapDlpActive ? 'true' : 'false');
        data.append('useStreamOptions', model.useStreamOptions ? 'true' : 'false');
        data.append('useUser', model.useUser ? 'true' : 'false');
        data.append('additionalParameters', JSON.stringify(model.additionalParameters || {}));
        if (model.credential) {
            data.append('credential', model.credential.identifier);
        }
        (model.sensitivityLabels ?? []).forEach((label: string) => {
            data.append('sensitivityLabels[]', label);
        });
        (model.allowedMimeTypes ?? []).forEach((mimeType: string) => {
            data.append('allowedMimeTypes[]', mimeType);
        });
        data.append('settings', JSON.stringify(model.settings));

        if (model.image instanceof File || model.image instanceof Blob) {
            const fileType = model.image.type.split('/')[1];
            const fileName = `upload.${fileType}`;
            data.append('image', new File([model.image], fileName, { type: model.image.type }));
        } else if (model.image && typeof model.image === 'string') {
            data.append('imageUrl', model.image);
        } else {
            // TODO: Micha fragen ob es gewollt ist, dass ein Bild nicht entfernt werden kann
            // data.append('image', '');
        }

        const type = modelIdentifier === 'new' ? 'create' : 'update/' + modelIdentifier;

        modelService.saveModel(type, data).then((response) => {
            modelIdentifier === 'new' && navigate('/admin/models/' + response.data.identifier);
            setModelSnapshot(response.data);
            dispatch(setReloadPrompts(true));
            notifySuccess();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }, [model, modelIdentifier, userSettings.isLoggedIn, navigate, notifySuccess, dispatch]);

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;
        if (modelIdentifier !== 'new' && modelIdentifier !== undefined) {
            modelService.getModel(modelIdentifier).then((response) => {
                const modelData = {
                    ...response.data,
                    costInput: response.data.costInput
                        ? parseFloat((response.data.costInput * 1000000).toFixed(10))
                        : 0,
                    costOutput: response.data.costOutput
                        ? parseFloat((response.data.costOutput * 1000000).toFixed(10))
                        : 0,
                };
                setModel(modelData);
                setImagePreview(response.data.image);
                setLoadingState((prevState) => ({ ...prevState, modelLoaded: true }));
                setModelSnapshot(response.data);
            });
        } else {
            const duplicateData = (location.state as any)?.duplicateData;
            if (duplicateData) {
                const modelData = {
                    ...duplicateData,
                    costInput: duplicateData.costInput
                        ? parseFloat((duplicateData.costInput * 1000000).toFixed(10))
                        : 0,
                    costOutput: duplicateData.costOutput
                        ? parseFloat((duplicateData.costOutput * 1000000).toFixed(10))
                        : 0,
                };
                setModel(modelData);
                if (duplicateData.image) {
                    setImagePreview(duplicateData.image);
                }
                setModelSnapshot(modelData);
            }
            setLoadingState((prevState) => ({ ...prevState, modelLoaded: true }));
        }

        modelService.getLlmProviders().then((response) => {
            setLlmProviders(response.data);
            setLoadingState((prevState) => ({ ...prevState, llmModelLoaded: true }));
        });

        credentialsService.getCredentials().then((response) => {
            setCredentials(response.data.data);
            setLoadingState((prevState) => ({ ...prevState, credentialsLoaded: true }));
        });

        settingsService.getAllMimeTypesAndFileExtensions().then((response) => {
            setAllMimeTypesWithFileExtensions(response.data);
        });
    }, [userSettings.isLoggedIn, modelIdentifier, location.state]);

    useEffect(() => {
        if (model.additionalParameters) {
            setAdditionalParamsText(JSON.stringify(model.additionalParameters, null, 2));
            setJsonError(null);
        }
    }, [model.additionalParameters]);

    const handleUpdateAdditionalParamsText = useCallback(
        (value: string) => {
            setAdditionalParamsText(value);
            if (jsonError) setJsonError(null);
        },
        [jsonError],
    );

    const handleAdditionalParamsBlur = useCallback(() => {
        try {
            const jsonValue = additionalParamsText.trim() === '' ? {} : JSON.parse(additionalParamsText);
            handleChangeModel('additionalParameters', jsonValue);
            setJsonError(null);
        } catch (error) {
            console.error('Invalid JSON:', error);
            setJsonError(i18n.translate({ id: 'error.invalidJson', sourceName: 'Model' }) || 'Ungültiges JSON-Format');
        }
    }, [additionalParamsText, handleChangeModel, i18n]);

    const ModelActions = useMemo(
        () => ({
            handleLlmProviderDropdownChange,
            handleImageChange,
            handleSave,
            handleChangeModel,
            updateModel,
            handleRemoveImage,
            handleUpdateAdditionalParamsText,
            handleAdditionalParamsBlur,
            handleCredentialDropdownChange,
            setMimeTypesCopied,
        }),
        [
            handleLlmProviderDropdownChange,
            handleImageChange,
            handleSave,
            handleChangeModel,
            updateModel,
            handleRemoveImage,
            handleUpdateAdditionalParamsText,
            handleAdditionalParamsBlur,
            handleCredentialDropdownChange,
            setMimeTypesCopied,
        ],
    );

    const handleNavigate = useCallback(
        (path: string) => {
            navigate(path);
        },
        [navigate],
    );

    return {
        ModelActions,
        handleNavigate,
        modelSnapshot,
        model,
        imagePreview,
        icapAddress,
        i18n,
        llmProviders,
        sensitivityLabels,
        loadingState,
        modelIdentifier,
        additionalParamsText,
        jsonError,
        allMimeTypesWithFileExtensions,
        credentials,
        mimeTypesCopied,
    };
};

export default useModel;
